/**
 * EN↔ES Language Pair Definition
 *
 * Defines resources for English and Spanish separately.
 * buildProfile() generates directional profiles:
 *   enEs = ES native → learn EN (UI in Spanish)
 *   esEn = EN native → learn ES (UI in English)
 */

import type { LanguagePairDefinition, LanguageProfile } from '../types'
import { buildProfile } from '../build-profile'

const PAIR: LanguagePairDefinition = {
  id: 'en-es',
  languages: {
    en: {
      lang: 'en',
      langName: 'English',
      shortCode: 'EN',
      scriptSample: 'Hello',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'US' },
          { key: 'phonetic_uk', label: 'UK' },
        ],
      },
      tts: {
        voices: [
          { accent: 'us', lang: 'en-US' },
          { accent: 'uk', lang: 'en-GB' },
        ],
        detectScript: /^$/,
      },
      ui: {
        inputPlaceholder: 'Enter words, phrases, or questions...',
        headingText: 'What do you want to learn today?',
        intentChips: [
          { label: 'List', intent: 'BATCH_LIST', suffix: 'how to say in Spanish' },
          { label: 'Compare', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in English.',
      },
    },
    es: {
      lang: 'es',
      langName: 'Spanish',
      shortCode: 'ES',
      scriptSample: 'Hola',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'ES' },
        ],
      },
      tts: {
        voices: [
          { accent: 'es', lang: 'es-ES' },
        ],
        detectScript: /[a-záéíóúñü]/i,
      },
      ui: {
        inputPlaceholder: 'Ingresa palabras, frases o preguntas...',
        headingText: '¿Qué quieres aprender hoy?',
        intentChips: [
          { label: 'Lista', intent: 'BATCH_LIST', suffix: 'cómo se dice en inglés' },
          { label: 'Comparar', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in Spanish.',
        languageHints: [
          'For nouns, always include the definite article (el/la/los/las) in "front" to indicate gender.',
          'Flag when a phrase triggers subjunctive mood — add grammar:subjunctive tag.',
          'For ser/estar and por/para distinctions, include a usage note in "definition".',
          'Tag regional variants when applicable: variant:latin-american or variant:castilian.',
        ].join('\n'),
      },
      cardMeta: {
        keys: ['gender', 'irregular', 'subjunctiveTrigger'],
        promptDescription: `"card_meta" (JSON object, required): Spanish-specific metadata:
- "gender": "m" or "f". Only for nouns.
- "irregular": boolean. Only for irregular verbs.
- "subjunctiveTrigger": boolean. True if the word/phrase typically triggers subjunctive mood.`,
      },
    },
  },
}

export const enEsPair = PAIR
export const enEs: LanguageProfile = buildProfile(PAIR, 'en')
export const esEn: LanguageProfile = buildProfile(PAIR, 'es')
